import { ReactNode } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

type ConversationStarterProps = {
  number: number;
  content: string;
  actionLabel?: string;
  // When set, the action renders as an icon-only button and actionLabel
  // becomes its tooltip and accessible name.
  actionIcon?: ReactNode;
  onAction?: () => void;
  isActionLoading?: boolean;
  isActionDisabled?: boolean;
};

export const ConversationStarter = (props: ConversationStarterProps) => {
  const isDisabled = props.isActionDisabled || props.isActionLoading;
  const loadingIcon = <CircularProgress size={16} color="inherit" />;

  const getAction = () => {
    if (!props.actionLabel || !props.onAction) return null;
    if (props.actionIcon) {
      return (
        <Tooltip title={props.actionLabel}>
          {/* span keeps the tooltip working while the button is disabled */}
          <Box component="span" flexShrink={0}>
            <IconButton
              aria-label={props.actionLabel}
              onClick={props.onAction}
              disabled={isDisabled}
            >
              {props.isActionLoading ? loadingIcon : props.actionIcon}
            </IconButton>
          </Box>
        </Tooltip>
      );
    }
    return (
      <Button
        variant="outlined"
        onClick={props.onAction}
        disabled={isDisabled}
        startIcon={props.isActionLoading ? loadingIcon : undefined}
        sx={{ flexShrink: 0 }}
      >
        {props.actionLabel}
      </Button>
    );
  };

  return (
    <Stack
      direction="row"
      justifySelf="stretch"
      alignItems="center"
      justifyContent="center"
      borderRadius=".75rem"
      padding=".75rem"
      columnGap=".75rem"
      className="border border-teamApp-card-accent-bg"
    >
      <Box
        display="flex"
        justifyContent="center"
        width="2.5rem"
        height="2.5rem"
        borderRadius=".5rem"
        padding=".5rem"
        className="bg-sapphire-05"
      >
        <Typography variant="cardTitle" className="text-teamApp-heading-accent">
          {props.number + 1}
        </Typography>
      </Box>
      <Box flex="1 1 0">
        <Typography variant="body1" className="text-teamApp-heading-accent">
          {props.content}
        </Typography>
      </Box>
      {getAction()}
    </Stack>
  );
};
